import React, { useState, useEffect, useRef, useMemo } from 'react';
import Layout from 'Component/Layouts/layout';
import {
  Box,
  Button,
  Typography,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  FormControlLabel,
  TextField,
  Autocomplete,
  Grid,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Alert,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Add as AddIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import rootAxios from 'requests/rootAxios';
import trainingTypes from 'Constants/trainingTypes';
import {
  fetchRegions,
  fetchRegionsBranches,
  fetchDailyEmployeesWhoJoined,
} from 'thunks/trainingThunks/thunk';
import { fetchPFConfigForTrainer } from 'reducer/productFinancingConfigSlice';
import EmailModal from 'Component/MultiStepTrainingForms/TrainingForm/EmailModal';
import EmailModalCollection from 'Component/MultiStepTrainingForms/TrainingForm/EmailModalCollection';
import EmailModalTrailblaze from 'Component/MultiStepTrainingForms/TrainingForm/EmailModalTrailblaze';
import EmailModalProductFinancing from 'Component/MultiStepTrainingForms/TrainingForm/EmailModalProductFinancing';
import AddEmployeeForm from 'Component/MultiStepTrainingForms/TrainingForm/AddEmployeeForm';

const CreateBatch = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(0);
  const [pendencyData, setPendencyData] = useState({});
  const [selectedEmployees, setSelectedEmployees] = useState({});
  const [showBatchForm, setShowBatchForm] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showEmailModalCollection, setShowEmailModalCollection] = useState(false);
  const [showEmailModalTrailblaze, setShowEmailModalTrailblaze] = useState(false);
  const [showEmailModalPF, setShowEmailModalPF] = useState(false);
  const [isCreatingBatch, setIsCreatingBatch] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showRegionBranchModal, setShowRegionBranchModal] = useState(false);
  const [showAddEmployeeModal, setShowAddEmployeeModal] = useState(false);
  const [batchFormData, setBatchFormData] = useState({
    batchType: 'offline',
    trainingType: '',
    trainingRoLocation: '',
    address: '',
    googleMapsLink: '',
    trainingLink: '',
    duration: '',
    description: '',
    dates: [
      {
        day: 'Day 1',
        date: (() => {
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
          return tomorrow.toISOString().split('T')[0] + 'T09:00';
        })(),
      },
    ],
    datesEnd: [{ date: '18:30' }],
  });
  const [emailConfig, setEmailConfig] = useState({
    totalSteps: 3,
    steps: [
      { to: [], cc: [] },
      { to: [], cc: [] },
      { to: [], cc: [] },
    ],
  });
  const [availableRegions, setAvailableRegions] = useState([]);
  const [availableBranches, setAvailableBranches] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [manuallyAddedEmployees, setManuallyAddedEmployees] = useState({});
  const [queryTrainingType, setQueryTrainingType] = useState(null);
  const queryParams = new URLSearchParams(location.search);
  const auditId = queryParams.get('auditId');
  // AbortController for cancelling pending API calls
  const [abortController, setAbortController] = useState(null);

  // Get loading state from Redux
  const { loading } = useSelector((state) => state.trainingSlice);

  // Sorting state
  const [sortConfig, setSortConfig] = useState({});

  // Get user data from auth context
  const userData = useSelector((state) => state.authSlice.userData);
  const pfTrainerConfig = useSelector((state) => state.pfConfigSlice.trainerConfig);

  // Fetch PF config for the logged-in trainer to decide if PF tab is visible.
  // fetchInitialData also awaits this before building the pendency tab list,
  // so we only need this effect to hydrate the Redux state on mount.
  useEffect(() => {
    dispatch(fetchPFConfigForTrainer());
  }, [dispatch]);

  // Training types for tabs - dynamically include corrective-training if in query params
  const trainingTypeTabs = useMemo(() => {
    const queryParams = new URLSearchParams(location.search);
    const trainingTypeParam = queryParams.get('trainingType');

    if (trainingTypeParam === 'corrective-training') {
      return [
        { key: 'CORRECTIVE_TRAINING', label: 'Corrective Training', value: 'corrective-training' },
      ];
    }

    return [
      { key: 'REGULAR_INDUCTION', label: 'Induction', value: trainingTypes.REGULAR_INDUCTION },
      { key: 'COLLECTION_TRAINING', label: 'Collection', value: trainingTypes.COLLECTION_TRAINING },
      // Individual Lending tab hidden — IL pendency is now shown within the Induction tab (backend merges IL roles into Induction).
      // {
      //   key: 'INDIVIDUAL_LENDING',
      //   label: 'Individual Lending',
      //   value: trainingTypes.INDIVIDUAL_LENDING,
      // },
      { key: 'PRAGATI_TRAINING', label: 'Pragati', value: trainingTypes.PRAGATI_TRAINING },
      { key: 'TRAILBLAZE_2_0', label: 'Trailblaze 2.0', value: trainingTypes.TRAILBLAZE_2_0 },
      // Product Financing tab shown only if trainer has an active PF config
      ...(pfTrainerConfig
        ? [
            {
              key: 'PRODUCT_FINANCING',
              label: 'Product Financing',
              value: trainingTypes.PRODUCT_FINANCING,
            },
          ]
        : []),
    ];
  }, [location.search, pfTrainerConfig]);

  // Date formatting function
  const formatDateOfJoining = (dateString) => {
    if (!dateString) return '';
    return dateString;
  };

  // Sorting functions
  const handleSort = (trainingTypeKey, field) => {
    setSortConfig((prev) => {
      const newConfig = { ...prev };
      if (newConfig[trainingTypeKey]?.field === field) {
        newConfig[trainingTypeKey] = {
          ...newConfig[trainingTypeKey],
          direction: newConfig[trainingTypeKey].direction === 'asc' ? 'desc' : 'asc',
        };
      } else {
        newConfig[trainingTypeKey] = { field, direction: 'asc' };
      }
      return newConfig;
    });
  };

  const getSortIcon = (trainingTypeKey, field) => {
    const config = sortConfig[trainingTypeKey];
    if (config?.field === field) {
      return config.direction === 'asc' ? (
        <ArrowUpwardIcon fontSize="small" />
      ) : (
        <ArrowDownwardIcon fontSize="small" />
      );
    }
    return null;
  };

  const getSortedEmployees = (trainingTypeKey) => {
    const employees = pendencyData[trainingTypeKey] || [];
    const config = sortConfig[trainingTypeKey];

    // First, sort by highlight status (highlighted rows first)
    let sortedEmployees = [...employees].sort((a, b) => {
      const aHighlighted = shouldHighlightRow(a);
      const bHighlighted = shouldHighlightRow(b);

      if (aHighlighted && !bHighlighted) return -1; // a comes first
      if (!aHighlighted && bHighlighted) return 1; // b comes first
      return 0; // both have same highlight status
    });

    // Then apply custom sorting within each group (highlighted and non-highlighted)
    if (config) {
      sortedEmployees = sortedEmployees.sort((a, b) => {
        // If highlight status is different, maintain highlight order
        const aHighlighted = shouldHighlightRow(a);
        const bHighlighted = shouldHighlightRow(b);

        if (aHighlighted !== bHighlighted) {
          return aHighlighted ? -1 : 1;
        }

        // If highlight status is the same, apply custom sorting
        let aValue = a[config.field];
        let bValue = b[config.field];

        // Handle date sorting — use effectivePendingDate (role-change date for promoted Pragati
        // employees) so promoted employees sort by when they became eligible, not DOJ.
        if (config.field === 'dateOfJoining') {
          aValue = new Date(a.effectivePendingDate || aValue || 0);
          bValue = new Date(b.effectivePendingDate || bValue || 0);
        }

        // Handle numeric sorting
        if (config.field === 'nominationCount') {
          aValue = parseInt(aValue) || 0;
          bValue = parseInt(bValue) || 0;
        }

        if (aValue < bValue) return config.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return config.direction === 'asc' ? 1 : -1;
        return 0;
      });
    } else {
      // Default sorting by date of joining in descending order within each highlight group
      sortedEmployees = sortedEmployees.sort((a, b) => {
        // If highlight status is different, maintain highlight order
        const aHighlighted = shouldHighlightRow(a);
        const bHighlighted = shouldHighlightRow(b);

        if (aHighlighted !== bHighlighted) {
          return aHighlighted ? -1 : 1;
        }

        // If highlight status is the same, sort by effective pending date (descending)
        const aDate = new Date(a.effectivePendingDate || a.dateOfJoining || 0);
        const bDate = new Date(b.effectivePendingDate || b.dateOfJoining || 0);
        return bDate - aDate; // Descending order (newest first)
      });
    }

    return sortedEmployees;
  };

  // Function to check if a row should be highlighted
  const shouldHighlightRow = (employee) => {
    const effectiveDate = employee.effectivePendingDate || employee.dateOfJoining;
    if (!effectiveDate) return false;

    const joiningDate = new Date(effectiveDate);
    const currentDate = new Date();
    const daysDifference = Math.floor((currentDate - joiningDate) / (1000 * 60 * 60 * 24));

    // Highlight if date of joining is more than 15 days or nomination count is more than 0
    return (
      daysDifference > 15 || (employee.nominationCount && parseInt(employee.nominationCount) > 0)
    );
  };

  // Read query parameters and set active tab
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const trainingTypeParam = queryParams.get('trainingType');
    const regionIdsParam = queryParams.get('regionIds');
    const branchesParam = queryParams.get('branches');

    // Store training type from query params
    if (trainingTypeParam) {
      setQueryTrainingType(trainingTypeParam);
      // Also set it in batchFormData
      setBatchFormData((prev) => ({
        ...prev,
        trainingType: trainingTypeParam,
      }));

      // Set active tab to corrective-training if that's the training type
      if (trainingTypeParam === 'corrective-training') {
        setActiveTab(0); // Corrective training will be the first (and only) tab
      }
    }

    // These will be used in fetchInitialData
    if (trainingTypeParam || regionIdsParam || branchesParam) {
      console.log('Query params detected:', { trainingTypeParam, regionIdsParam, branchesParam });
    }
  }, [location.search]);

  useEffect(() => {
    fetchInitialData();

    // Cleanup function to abort any pending requests when component unmounts
    return () => {
      if (abortController) {
        abortController.abort();
      }
    };
  }, []);

  // Add cleanup for loading state when component unmounts
  useEffect(() => {
    return () => {
      // Component unmounting cleanup
    };
  }, []);

  // Add a safety timeout to ensure loading doesn't get stuck
  useEffect(() => {
    if (loading) {
      const timeout = setTimeout(() => {
        console.warn('Loading timeout reached');
        console.log('Safety timeout: Loading timeout after 30 seconds');
      }, 30000); // 30 seconds timeout

      return () => clearTimeout(timeout);
    }
  }, [loading]);

  // Add a shorter timeout for more aggressive cleanup
  useEffect(() => {
    if (loading) {
      const shortTimeout = setTimeout(() => {
        console.warn('Short loading timeout reached, checking if loading should be false');
        // Only log if loading is still true after 10 seconds
        if (loading) {
          console.log('Short timeout: Loading still true after 10 seconds');
        }
      }, 10000); // 10 seconds timeout

      return () => clearTimeout(shortTimeout);
    }
  }, [loading]);

  // Log loading state changes for debugging
  useEffect(() => {
    console.log('Loading state changed to:', loading, 'at:', new Date().toISOString());

    // Add stack trace to see where loading is being set
    if (loading) {
      console.trace('Loading set to true from:');
    } else {
      console.log('Loading set to false at:', new Date().toISOString());
    }
  }, [loading]);

  // Add a counter to track how many times loading is set to true vs false
  const loadingCounter = useRef({ true: 0, false: 0 });

  useEffect(() => {
    if (loading) {
      loadingCounter.current.true++;
    } else {
      loadingCounter.current.false++;
    }
    console.log(
      'Loading counter - true:',
      loadingCounter.current.true,
      'false:',
      loadingCounter.current.false
    );
  }, [loading]);

  const fetchInitialData = async () => {
    let newAbortController = null;
    try {
      console.log('Starting to fetch initial data...');

      // Fetch PF config early so we know whether to include the PF tab in the
      // pendency loop below.  We dispatch it here (in addition to the mount
      // useEffect) so we have the resolved value synchronously before we build
      // the tabs list — avoiding the stale-closure problem in fetchPendencyData.
      let resolvedPFConfig = null;
      try {
        resolvedPFConfig = await dispatch(fetchPFConfigForTrainer()).unwrap();
      } catch (_) {
        // No PF config for this trainer — that's fine, tab just won't appear.
      }

      // Read query parameters
      const queryParams = new URLSearchParams(location.search);
      const trainingTypeParam = queryParams.get('trainingType');
      const regionIdsParam = queryParams.get('regionIds');
      const branchesParam = queryParams.get('branches');

      // Parse query params
      const queryRegionIds = regionIdsParam ? regionIdsParam.split(',').filter(Boolean) : [];
      const queryBranchIds = branchesParam ? branchesParam.split(',').filter(Boolean) : [];

      // Abort any previous request
      if (abortController) {
        abortController.abort();
      }

      // Create new AbortController for this request
      newAbortController = new AbortController();
      setAbortController(newAbortController);

      // Fetch regions
      const regionsResponse = await rootAxios.get('/user/getRegion', {
        signal: newAbortController.signal,
      });

      // Check if request was aborted
      if (newAbortController.signal.aborted) {
        console.log('Initial data fetch was aborted');
        return;
      }

      console.log('Regions response:', regionsResponse?.data);

      if (regionsResponse?.data?.data?.regions) {
        const allRegions = regionsResponse.data.data.regions;
        console.log('Setting regions:', allRegions);
        console.log('First region structure:', allRegions[0]);
        setAvailableRegions(allRegions);

        // Filter regions based on query params if provided
        let selectedRegionsList = allRegions;
        if (queryRegionIds.length > 0) {
          selectedRegionsList = allRegions.filter((region) =>
            queryRegionIds.includes(region.regionId?.toString())
          );
          console.log('Filtered regions based on query params:', selectedRegionsList);
        } else {
          // Select all regions by default if no query params
          selectedRegionsList = allRegions;
        }
        setSelectedRegions(selectedRegionsList);

        if (allRegions.length === 0) {
          toast.warning('No regions found. Please contact administrator.');
          return;
        }

        // If we have branch IDs in query params, fetch branches from ALL regions first
        // Otherwise, fetch branches only for selected regions
        let regionIdsForBranchFetch = [];
        if (queryBranchIds.length > 0) {
          // Fetch branches from all regions to ensure we get the branches we need
          regionIdsForBranchFetch = allRegions.map((region) => region.regionId);
          console.log(
            'Fetching branches from ALL regions (query has branch IDs):',
            regionIdsForBranchFetch
          );
        } else {
          // Fetch branches only for selected regions
          regionIdsForBranchFetch = selectedRegionsList.map((region) => region.regionId);
          console.log('Fetching branches for selected region IDs:', regionIdsForBranchFetch);
        }

        const branchesResponse = await rootAxios.post(
          '/user/getRegionBatches',
          {
            regions: regionIdsForBranchFetch,
          },
          {
            signal: newAbortController.signal,
          }
        );

        // Check if request was aborted
        if (newAbortController.signal.aborted) {
          console.log('Initial data fetch was aborted');
          return;
        }

        console.log('Branches response:', branchesResponse?.data);

        if (branchesResponse?.data?.data) {
          const allBranches = branchesResponse.data.data;
          console.log('Setting branches:', allBranches);
          console.log('First branch structure:', allBranches[0]);
          setAvailableBranches(allBranches);

          // Filter branches based on query params if provided
          let selectedBranchesList = allBranches;
          let finalSelectedRegionsList = selectedRegionsList; // Use the regions list from above

          if (queryBranchIds.length > 0) {
            // Filter branches by matching BRANCH_ID with query branch IDs
            // Try multiple field names to match branch IDs
            selectedBranchesList = allBranches.filter((branch) => {
              const branchId = branch.BRANCH_ID?.toString();
              const auditLocationId = branch.auditLocationId?.toString();
              const branchIdField = branch.branchId?.toString();

              // Check if any of the branch ID fields match the query branch IDs
              const matches = queryBranchIds.some((queryId) => {
                return (
                  queryId === branchId || queryId === auditLocationId || queryId === branchIdField
                );
              });

              if (matches) {
                console.log('Matched branch:', {
                  branchId,
                  auditLocationId,
                  branchIdField,
                  branch,
                });
              }
              return matches;
            });
            console.log('All branches count:', allBranches.length);
            console.log('Query branch IDs:', queryBranchIds);
            console.log('Filtered branches count:', selectedBranchesList.length);
            console.log('Filtered branches based on query params:', selectedBranchesList);

            if (selectedBranchesList.length === 0 && queryBranchIds.length > 0) {
              console.warn(
                'No branches matched the query branch IDs. Available branch fields:',
                allBranches[0] ? Object.keys(allBranches[0]) : 'No branches available'
              );
            }

            // If we have branch IDs but no region IDs, determine regions from branches
            if (queryRegionIds.length === 0 && selectedBranchesList.length > 0) {
              // Extract unique region IDs from selected branches
              const regionIdsFromBranches = [
                ...new Set(
                  selectedBranchesList
                    .map((branch) => branch.REGION_ID || branch.regionId)
                    .filter(Boolean)
                ),
              ];

              if (regionIdsFromBranches.length > 0) {
                // Filter regions based on branches' region IDs
                const regionsFromBranches = allRegions.filter((region) =>
                  regionIdsFromBranches.includes(region.regionId?.toString())
                );
                if (regionsFromBranches.length > 0) {
                  console.log('Auto-selected regions based on branches:', regionsFromBranches);
                  setSelectedRegions(regionsFromBranches);
                  finalSelectedRegionsList = regionsFromBranches;
                }
              }
            }
          } else {
            // Select all branches by default if no query params
            selectedBranchesList = allBranches;
          }

          // Set the selected branches - this will trigger the auto-selection
          setSelectedBranches(selectedBranchesList);
          console.log('Final selected branches:', selectedBranchesList);

          if (allBranches.length === 0) {
            toast.warning(
              'No branches found for the selected regions. Please contact administrator.'
            );
            return;
          }

          // Build the definitive tabs list using the already-resolved PF config so
          // that the pendency loop includes Product Financing without depending on
          // whether the useMemo has re-run yet.
          const tabsForPendency = [
            {
              key: 'REGULAR_INDUCTION',
              label: 'Induction',
              value: trainingTypes.REGULAR_INDUCTION,
            },
            {
              key: 'COLLECTION_TRAINING',
              label: 'Collection',
              value: trainingTypes.COLLECTION_TRAINING,
            },
            // Individual Lending tab hidden — IL pendency is now shown within the Induction tab (backend merges IL roles into Induction).
            // {
            //   key: 'INDIVIDUAL_LENDING',
            //   label: 'Individual Lending',
            //   value: trainingTypes.INDIVIDUAL_LENDING,
            // },
            { key: 'PRAGATI_TRAINING', label: 'Pragati', value: trainingTypes.PRAGATI_TRAINING },
            { key: 'TRAILBLAZE_2_0', label: 'Trailblaze 2.0', value: trainingTypes.TRAILBLAZE_2_0 },
            ...(resolvedPFConfig
              ? [
                  {
                    key: 'PRODUCT_FINANCING',
                    label: 'Product Financing',
                    value: trainingTypes.PRODUCT_FINANCING,
                  },
                ]
              : []),
          ];

          // Fetch pendency data for each training type with the fetched regions and branches
          console.log('Fetching pendency data with regions and branches...');
          await fetchPendencyData(finalSelectedRegionsList, selectedBranchesList, tabsForPendency);
        } else {
          toast.warning(
            'No branches found for the selected regions. Please contact administrator.'
          );
        }
      } else {
        toast.warning('No regions found. Please contact administrator.');
      }
    } catch (error) {
      // Don't show error if request was aborted
      if (error.name === 'AbortError' || error?.name === 'CanceledError') {
        console.log('Initial data fetch was aborted');
        return;
      }
      console.error('Error fetching initial data:', error);
      toast.error('Failed to fetch initial data');
    }
  };

  const fetchPendencyData = async (regions, branches, tabsOverride) => {
    let newAbortController = null;
    try {
      // Abort any previous request
      if (abortController) {
        console.log('fetchPendencyData: Aborting previous request');
        abortController.abort();
      }

      // Create new AbortController for this request
      newAbortController = new AbortController();
      setAbortController(newAbortController);

      console.log('fetchPendencyData called with:', { regions, branches });

      // Validate that we have regions and branches
      if (!regions || regions.length === 0 || !branches || branches.length === 0) {
        console.warn('No regions or branches available for pendency data fetch');
        setPendencyData({});
        return;
      }

      // If training type is corrective-training, skip pendency data fetch
      if (queryTrainingType === 'corrective-training') {
        console.log('Skipping pendency data fetch for corrective-training');
        setPendencyData({ CORRECTIVE_TRAINING: [] });
        return;
      }

      // Use the explicitly provided tabs list if given (avoids stale-closure issues when
      // pfTrainerConfig hasn't been reflected in the useMemo yet), otherwise fall back to
      // the current memoised value.
      const tabsToFetch = tabsOverride || trainingTypeTabs;

      console.log('regions', regions);
      console.log('branches', branches);
      const pendencyDataTemp = {};

      for (const tab of tabsToFetch) {
        // Check if request was aborted
        if (newAbortController.signal.aborted) {
          console.log('Request was aborted, stopping pendency data fetch');
          console.log('fetchPendencyData: Request aborted in loop, returning');
          return;
        }

        const payload = {
          trainingType: tab.value,
          region: regions.map((region) => ({ id: region.regionId, label: region.regionName })),
          branches: branches.map((branch) => ({ id: branch.BRANCH_ID, label: branch.BRANCH_NAME })),
        };

        console.log(`Fetching data for ${tab.label} with payload:`, payload);

        try {
          const response = await dispatch(fetchDailyEmployeesWhoJoined(payload)).unwrap();

          console.log(`Response for ${tab.label}:`, response);

          if (response?.employees) {
            pendencyDataTemp[tab.key] = response.employees;
            console.log(`Added ${response.employees.length} employees for ${tab.label}`);
          } else {
            pendencyDataTemp[tab.key] = [];
            console.log(`No employees found for ${tab.label}`);
          }
        } catch (tabError) {
          // Don't show error if request was aborted
          console.log('tabError', tabError);
          if (tabError === 'Request was cancelled' || tabError?.name === 'CanceledError') {
            console.log(`Request for ${tab.label} was aborted`);
            return;
          }
          console.error(`Error fetching pendency data for ${tab.label}:`, tabError);
          pendencyDataTemp[tab.key] = [];
          toast.error(`Failed to fetch ${tab.label} pendency data`);
        }
      }

      // Only update state if request wasn't aborted
      if (!newAbortController.signal.aborted) {
        console.log('Final pendency data:', pendencyDataTemp);
        setPendencyData(pendencyDataTemp);
        console.log('Pendency data updated successfully');
      }
    } catch (error) {
      // Don't show error if request was aborted
      if (error.name === 'AbortError' || error?.name === 'CanceledError') {
        console.log('Pendency data fetch was aborted');
        return;
      }
      console.error('Error fetching pendency data:', error);
      toast.error('Failed to fetch pendency data');
    }
  };

  const handleTabChange = (event, newValue) => {
    // Clear the selection for the tab we're leaving (on unmount of its table)
    const leavingTabKey = trainingTypeTabs[activeTab]?.key;
    if (leavingTabKey) {
      setSelectedEmployees((prev) => ({
        ...prev,
        [leavingTabKey]: [],
      }));
    }
    setActiveTab(newValue);
  };

  const handleSelectAllEmployees = (trainingTypeKey, checked) => {
    const employees = pendencyData[trainingTypeKey] || [];
    const newSelected = { ...selectedEmployees };

    if (checked) {
      newSelected[trainingTypeKey] = employees.map((emp) => emp.employeeId);
    } else {
      newSelected[trainingTypeKey] = [];
    }

    setSelectedEmployees(newSelected);
  };

  const handleSelectEmployee = (trainingTypeKey, employeeId, checked) => {
    const newSelected = { ...selectedEmployees };

    if (!newSelected[trainingTypeKey]) {
      newSelected[trainingTypeKey] = [];
    }

    if (checked) {
      if (!newSelected[trainingTypeKey].includes(employeeId)) {
        newSelected[trainingTypeKey].push(employeeId);
      }
    } else {
      newSelected[trainingTypeKey] = newSelected[trainingTypeKey].filter((id) => id !== employeeId);
    }

    setSelectedEmployees(newSelected);
  };

  const getTotalSelectedEmployees = () => {
    return Object.values(selectedEmployees).reduce(
      (total, employees) => total + employees.length,
      0
    );
  };

  const handleCreateBatch = () => {
    // For corrective training, allow creating batch without employees
    if (queryTrainingType !== 'corrective-training' && getTotalSelectedEmployees() === 0) {
      toast.error('Please select at least one employee');
      return;
    }
    console.log('handleCreateBatch: Opening batch form');
    setShowBatchForm(true);
  };

  const handleBatchFormSubmit = (formData) => {
    // Use training type from query params if available, otherwise use active tab
    const trainingTypeToUse =
      queryTrainingType || batchFormData.trainingType || trainingTypeTabs[activeTab].value;
    const updatedFormData = {
      ...formData,
      trainingType: trainingTypeToUse,
    };

    console.log('handleBatchFormSubmit: Setting form data with training type:', updatedFormData);
    setBatchFormData(updatedFormData);
    setShowBatchForm(false);

    // Show email modal based on training type
    const currentTrainingType = trainingTypeToUse;
    if (
      currentTrainingType === trainingTypes.COLLECTION_TRAINING ||
      currentTrainingType === 'corrective-training'
    ) {
      setShowEmailModalCollection(true);
    } else if (currentTrainingType === trainingTypes.TRAILBLAZE_2_0) {
      setShowEmailModalTrailblaze(true);
    } else if (currentTrainingType === trainingTypes.PRODUCT_FINANCING) {
      setShowEmailModalPF(true);
    } else {
      setShowEmailModal(true);
    }
  };

  const handleEmailSubmit = (emailData) => {
    // Transform email data to match the required format
    let formattedEmailConfig;

    const currentTabValue = trainingTypeTabs[activeTab]?.value;
    if (
      currentTabValue === trainingTypes.COLLECTION_TRAINING ||
      currentTabValue === 'corrective-training'
    ) {
      formattedEmailConfig = {
        totalSteps: 2,
        steps: [
          { to: emailData.stage1.to, cc: emailData.stage1.cc },
          { to: emailData.stage2.to, cc: emailData.stage2.cc },
        ],
      };
    } else if (currentTabValue === trainingTypes.TRAILBLAZE_2_0) {
      formattedEmailConfig = {
        totalSteps: 1,
        steps: [{ to: emailData.stage1.to, cc: emailData.stage1.cc }],
      };
    } else if (currentTabValue === trainingTypes.INDIVIDUAL_LENDING) {
      formattedEmailConfig = {
        totalSteps: 3,
        steps: [
          { to: emailData.stage1.to, cc: emailData.stage1.cc },
          { to: emailData.stage2.to, cc: emailData.stage2.cc },
          { to: emailData.stage3.to, cc: emailData.stage3.cc },
        ],
      };
    } else if (currentTabValue === trainingTypes.PRODUCT_FINANCING) {
      // L1/L2 approver emails are fetched by backend from pfConfig; store CC + intimation (step 3)
      formattedEmailConfig = {
        totalSteps: 3,
        steps: [
          { to: [], cc: emailData.stage1.cc },
          { to: [], cc: emailData.stage2.cc },
          { to: emailData.stage3.to, cc: emailData.stage3.cc },
        ],
      };
    } else {
      formattedEmailConfig = {
        totalSteps: 3,
        steps: [
          { to: emailData.stage1.to, cc: emailData.stage1.cc },
          { to: emailData.stage2.to, cc: emailData.stage2.cc },
          { to: emailData.stage3.to, cc: emailData.stage3.cc },
        ],
      };
    }

    setEmailConfig(formattedEmailConfig);
    setShowEmailModal(false);
    setShowEmailModalCollection(false);
    setShowEmailModalTrailblaze(false);
    setShowEmailModalPF(false);

    // Show review modal
    setShowReviewModal(true);
  };

  const handleCreateFinalBatch = async () => {
    if (isCreatingBatch) return;
    try {
      setIsCreatingBatch(true);
      console.log('handleCreateFinalBatch: Starting batch creation');

      // Validate required fields
      if (
        !batchFormData.trainingType ||
        (batchFormData.batchType === 'offline' && !batchFormData.trainingRoLocation) ||
        !batchFormData.duration
      ) {
        toast.error('Please fill in all required fields');
        return;
      }

      // Validate batch type specific fields
      if (batchFormData.batchType === 'offline') {
        if (!batchFormData.address) {
          toast.error('Please fill in the address for offline training');
          return;
        }
        if (!batchFormData.googleMapsLink) {
          toast.error('Please provide the Google Maps Link for offline training');
          return;
        }
      } else if (batchFormData.batchType === 'online') {
        if (!batchFormData.trainingLink) {
          toast.error('Please provide the online training link');
          return;
        }
      }

      // Validate email configuration
      // For Product Financing, steps 1 & 2 have auto-configured approvers (no to needed) — only validate step 3
      const currentTabValueForValidation = trainingTypeTabs[activeTab]?.value;
      const hasValidEmailConfig =
        currentTabValueForValidation === trainingTypes.PRODUCT_FINANCING
          ? emailConfig.steps[2]?.to?.length > 0
          : emailConfig.steps.every((step) => step.to.length > 0);
      if (!hasValidEmailConfig) {
        toast.error('Please configure email recipients for all steps');
        return;
      }

      // Prepare batch data
      const currentTrainingType =
        queryTrainingType || batchFormData.trainingType || trainingTypeTabs[activeTab]?.value;
      // For corrective training, we might not have employees selected in tabs, so use empty array as fallback
      const selectedEmployeeIds = selectedEmployees[trainingTypeTabs[activeTab]?.key] || [];

      // For corrective training, allow creating batch without employees (they can be added manually)
      if (currentTrainingType !== 'corrective-training' && selectedEmployeeIds.length === 0) {
        toast.error('Please select at least one employee for the batch');
        return;
      }
      console.log('selectedBranches', selectedBranches);
      const batchPayload = {
        trainingFormDetail: {
          trainingType: currentTrainingType,
          batchType: batchFormData.batchType,
          trainingRoLocation: batchFormData.trainingRoLocation,
          address: batchFormData.batchType === 'offline' ? batchFormData.address : '',
          googleMapsLink: batchFormData.batchType === 'offline' ? batchFormData.googleMapsLink : '',
          trainingLink: batchFormData.batchType === 'online' ? batchFormData.trainingLink : '',
          duration: batchFormData.duration,
          description: batchFormData.description,
          region: selectedRegions.map((region) => ({
            id: region.regionId,
            label: region.regionName,
          })),
          branch: selectedBranches.map((branch) => ({
            id: branch.BRANCH_ID,
            label: branch.BRANCH_NAME,
          })),
          batchTrainees: selectedEmployeeIds,
          emailConfig: emailConfig,
          dates: batchFormData.dates || [
            {
              day: 'Day 1',
              date: (() => {
                const tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + 1);
                return tomorrow.toISOString().split('T')[0] + 'T09:00';
              })(),
            },
          ],
          datesEnd: batchFormData.datesEnd || [{ date: '18:30' }],
          auditId: auditId,
        },
      };

      const response = await rootAxios.post('/user/createBatch', batchPayload);

      if (response?.data?.statusCode === 200) {
        toast.success('Batch created successfully!');
        setShowReviewModal(false);

        // window.location.href = '/training/AllTrainings';
      } else {
        toast.error(response?.data?.message || 'Failed to create batch');
      }
    } catch (error) {
      console.error('Error creating batch:', error);
      const errorMessage =
        error.response?.data?.message || error.message || 'Failed to create batch';
      toast.error(errorMessage);
    } finally {
      setIsCreatingBatch(false);
    }
  };

  const downloadPendencyReport = (trainingTypeKey, format = 'excel') => {
    const employees = pendencyData[trainingTypeKey] || [];
    if (employees.length === 0) {
      toast.info('No data to download');
      return;
    }

    if (format === 'excel') {
      // Convert to CSV format
      const headers = [
        'Employee ID',
        'Name',
        'Function Title',
        'Region',
        'Branch',
        'Date of Joining',
        'Nomination Count',
        'Training Type',
      ];
      const csvContent = [
        headers.join(','),
        ...employees.map((emp) =>
          [
            emp.employeeId || '',
            emp.employeeName || '',
            emp.funcTitleName || '',
            emp.regionName || '',
            emp.branchName || '',
            formatDateOfJoining(emp.effectivePendingDate || emp.dateOfJoining),
            emp.nominationCount || '0',
            trainingTypeTabs.find((tab) => tab.key === trainingTypeKey)?.label || '',
          ].join(',')
        ),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pendency_report_${trainingTypeKey}_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    }
  };

  const downloadAllPendencyReport = (format = 'excel') => {
    const allEmployees = Object.values(pendencyData).flat();
    if (allEmployees.length === 0) {
      toast.info('No data to download');
      return;
    }

    if (format === 'excel') {
      const headers = [
        'Employee ID',
        'Name',
        'Function Title',
        'Region',
        'Branch',
        'Date of Joining',
        'Nomination Count',
        'Training Type',
      ];
      const csvContent = [
        headers.join(','),
        ...allEmployees.map((emp) =>
          [
            emp.employeeId || '',
            emp.employeeName || '',
            emp.funcTitleName || '',
            emp.regionName || '',
            emp.branchName || '',
            formatDateOfJoining(emp.effectivePendingDate || emp.dateOfJoining),
            emp.nominationCount || '0',
            emp.trainingType || '',
          ].join(',')
        ),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `all_pendency_report_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    }
  };

  const handleRefreshPendencyData = async () => {
    try {
      console.log('handleRefreshPendencyData: Starting refresh');
      console.log(
        'handleRefreshPendencyData: Calling fetchPendencyData with current regions and branches'
      );
      await fetchPendencyData(selectedRegions, selectedBranches);
      console.log('handleRefreshPendencyData: fetchPendencyData completed');
      toast.success('Pendency data refreshed successfully');
    } catch (error) {
      // Don't show error if request was aborted
      if (error.name === 'AbortError' || error?.name === 'CanceledError') {
        console.log('Refresh pendency data was aborted');
        return;
      }
      console.error('Error refreshing pendency data:', error);
      toast.error('Failed to refresh pendency data');
    }
    // Note: fetchPendencyData will handle Redux loading state
  };

  const handleAddNewEmployee = (employeeData) => {
    console.log('handleAddNewEmployee called with:', employeeData);

    if (employeeData && employeeData.employees && employeeData.employees.length > 0) {
      const currentTrainingTypeKey = trainingTypeTabs[activeTab].key;

      // Add employees to pendencyData so they appear in the table
      const currentPendencyData = { ...pendencyData };
      const existingEmployees = currentPendencyData[currentTrainingTypeKey] || [];

      // Filter out employees that are already in the pendency data
      const newEmployees = employeeData.employees.filter(
        (newEmp) =>
          !existingEmployees.some((existingEmp) => existingEmp.employeeId === newEmp.employeeId)
      );

      if (newEmployees.length > 0) {
        // Add new employees to pendency data
        currentPendencyData[currentTrainingTypeKey] = [...existingEmployees, ...newEmployees];
        setPendencyData(currentPendencyData);

        // Track manually added employees
        const currentManuallyAdded = { ...manuallyAddedEmployees };
        if (!currentManuallyAdded[currentTrainingTypeKey]) {
          currentManuallyAdded[currentTrainingTypeKey] = new Set();
        }
        newEmployees.forEach((emp) => {
          currentManuallyAdded[currentTrainingTypeKey].add(emp.employeeId);
        });
        setManuallyAddedEmployees(currentManuallyAdded);

        // Automatically select the newly added employees
        const newSelected = { ...selectedEmployees };
        if (!newSelected[currentTrainingTypeKey]) {
          newSelected[currentTrainingTypeKey] = [];
        }

        const newEmployeeIds = newEmployees.map((emp) => emp.employeeId);
        const existingSelectedIds = newSelected[currentTrainingTypeKey];
        const uniqueNewIds = newEmployeeIds.filter((id) => !existingSelectedIds.includes(id));

        newSelected[currentTrainingTypeKey] = [...existingSelectedIds, ...uniqueNewIds];
        setSelectedEmployees(newSelected);

        setShowAddEmployeeModal(false);
        toast.success(
          `Added ${newEmployees.length} new employee(s) to the table and selected them`
        );
      } else {
        toast.info('All selected employees are already in the table');
      }
    } else {
      toast.warning('No employees were selected');
    }
  };

  const renderPendencyTable = (trainingTypeKey) => {
    const employees = pendencyData[trainingTypeKey] || [];
    const selectedEmployeeIds = selectedEmployees[trainingTypeKey] || [];

    if (employees.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="textSecondary">
            No pending employees found for this training type
          </Typography>
        </Box>
      );
    }

    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ verticalAlign: 'top' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={
                        selectedEmployeeIds.length === employees.length && employees.length > 0
                      }
                      indeterminate={
                        selectedEmployeeIds.length > 0 &&
                        selectedEmployeeIds.length < employees.length
                      }
                      onChange={(e) => handleSelectAllEmployees(trainingTypeKey, e.target.checked)}
                    />
                  }
                  label=""
                  sx={{ margin: 0 }}
                />
              </TableCell>
              <TableCell
                sx={{
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                }}
                onClick={() => handleSort(trainingTypeKey, 'employeeId')}
              >
                <Tooltip title="Click to sort by Employee ID">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Employee ID
                    {getSortIcon(trainingTypeKey, 'employeeId')}
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell
                sx={{
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                }}
                onClick={() => handleSort(trainingTypeKey, 'employeeName')}
              >
                <Tooltip title="Click to sort by Name">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Name
                    {getSortIcon(trainingTypeKey, 'employeeName')}
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell
                sx={{
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                }}
                onClick={() => handleSort(trainingTypeKey, 'funcTitleName')}
              >
                <Tooltip title="Click to sort by Function Title">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Function Title
                    {getSortIcon(trainingTypeKey, 'funcTitleName')}
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell
                sx={{
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                }}
                onClick={() => handleSort(trainingTypeKey, 'regionName')}
              >
                <Tooltip title="Click to sort by Region">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Region
                    {getSortIcon(trainingTypeKey, 'regionName')}
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell
                sx={{
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                }}
                onClick={() => handleSort(trainingTypeKey, 'branchName')}
              >
                <Tooltip title="Click to sort by Branch">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Branch
                    {getSortIcon(trainingTypeKey, 'branchName')}
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell
                sx={{
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                }}
                onClick={() => handleSort(trainingTypeKey, 'dateOfJoining')}
              >
                <Tooltip title="Click to sort by Date of Joining">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Date of Joining
                    {getSortIcon(trainingTypeKey, 'dateOfJoining')}
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell>Date of Role Change</TableCell>
              <TableCell
                sx={{
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                }}
                onClick={() => handleSort(trainingTypeKey, 'nominationCount')}
              >
                <Tooltip title="Click to sort by Nomination Count">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Nomination Count
                    {getSortIcon(trainingTypeKey, 'nominationCount')}
                  </Box>
                </Tooltip>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {getSortedEmployees(trainingTypeKey).map((employee) => (
              <TableRow
                key={employee.employeeId}
                sx={{
                  backgroundColor: shouldHighlightRow(employee)
                    ? 'rgba(255, 192, 203, 0.3)'
                    : 'inherit',
                  '&:hover': {
                    backgroundColor: shouldHighlightRow(employee)
                      ? 'rgba(255, 192, 203, 0.5)'
                      : 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedEmployeeIds.includes(employee.employeeId)}
                    onChange={(e) =>
                      handleSelectEmployee(trainingTypeKey, employee.employeeId, e.target.checked)
                    }
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {employee.employeeId}
                    {manuallyAddedEmployees[trainingTypeKey]?.has(employee.employeeId) && (
                      <Chip
                        label="Added"
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell>{employee.employeeName}</TableCell>
                <TableCell>{employee.funcTitleName}</TableCell>
                <TableCell>{employee.regionName}</TableCell>
                <TableCell>{employee.branchName}</TableCell>
                <TableCell>{employee.effectivePendingDate || employee.dateOfJoining}</TableCell>
                <TableCell>{employee.dateOfRoleChange || ''}</TableCell>
                <TableCell>{employee.nominationCount}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
          Create New Batch
        </Typography>

        {!showBatchForm ? (
          <>
            {/* Summary Cards */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <Card sx={{ minWidth: 200 }}>
                <CardContent>
                  <Typography variant="h6" color="primary">
                    Total Pending
                  </Typography>
                  <Typography variant="h4">
                    {Object.values(pendencyData).reduce(
                      (total, employees) => total + employees.length,
                      0
                    )}
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ minWidth: 200 }}>
                <CardContent>
                  <Typography variant="h6" color="success.main">
                    Selected Employees
                  </Typography>
                  <Typography variant="h4">{getTotalSelectedEmployees()}</Typography>
                </CardContent>
              </Card>
              <Card
                sx={{
                  minWidth: 200,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 3,
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  },
                }}
                onClick={() => setShowRegionBranchModal(true)}
              >
                <CardContent>
                  <Typography variant="h6" color="info.main">
                    Available Regions
                  </Typography>
                  <Typography variant="h4">{selectedRegions.length}</Typography>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ mt: 1, display: 'block' }}
                  >
                    Click to modify selection
                  </Typography>
                </CardContent>
              </Card>
              <Card
                sx={{
                  minWidth: 200,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 3,
                    backgroundColor: 'rgba(255, 152, 0, 0.04)',
                  },
                }}
                onClick={() => setShowRegionBranchModal(true)}
              >
                <CardContent>
                  <Typography variant="h6" color="warning.main">
                    Available Branches
                  </Typography>
                  <Typography variant="h4">{selectedBranches.length}</Typography>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ mt: 1, display: 'block' }}
                  >
                    Click to modify selection
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            {/* Download All Button and Refresh */}
            <Box
              sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => {
                  console.log('Refresh button clicked');
                  console.log('Current selectedRegions:', selectedRegions);
                  console.log('Current selectedBranches:', selectedBranches);
                  handleRefreshPendencyData();
                }}
                disabled={loading}
              >
                Refresh Pendency Data
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => downloadAllPendencyReport('excel')}
                disabled={Object.values(pendencyData).every((employees) => employees.length === 0)}
              >
                Download All Pendency Report
              </Button>
            </Box>

            {/* Training Type Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs value={activeTab} onChange={handleTabChange} aria-label="training type tabs">
                {trainingTypeTabs.map((tab, index) => (
                  <Tab
                    key={tab.key}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">{tab.label}</Typography>
                        <Chip
                          label={pendencyData[tab.key]?.length || 0}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                    }
                  />
                ))}
              </Tabs>
            </Box>

            {/* Download Tab Report Button */}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => downloadPendencyReport(trainingTypeTabs[activeTab].key, 'excel')}
                disabled={
                  !pendencyData[trainingTypeTabs[activeTab].key] ||
                  pendencyData[trainingTypeTabs[activeTab].key].length === 0
                }
              >
                Download {trainingTypeTabs[activeTab].label} Pendency Report
              </Button>
            </Box>

            {/* Pendency Table */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {renderPendencyTable(trainingTypeTabs[activeTab].key)}
                {Object.values(pendencyData).every((employees) => employees.length === 0) && (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <InfoIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="textSecondary" sx={{ mb: 1 }}>
                      No Pending Employees Found
                    </Typography>
                    <Typography variant="body1" color="textSecondary">
                      There are currently no employees with pending training for the selected
                      regions and branches.
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {/* Action Buttons */}
            <Box
              sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}
            >
              <Button
                variant="outlined"
                size="large"
                startIcon={<AddIcon />}
                onClick={() => setShowAddEmployeeModal(true)}
                sx={{ px: 4, py: 1.5 }}
              >
                Add New Employee
              </Button>
              <Button
                variant="contained"
                size="large"
                startIcon={<AddIcon />}
                onClick={handleCreateBatch}
                disabled={getTotalSelectedEmployees() === 0}
                sx={{ px: 4, py: 1.5 }}
              >
                Create Batch ({getTotalSelectedEmployees()} employees)
              </Button>
            </Box>

            {/* Info about selected employees */}
            {getTotalSelectedEmployees() > 0 && (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <Typography variant="body2" color="textSecondary">
                  {getTotalSelectedEmployees()} employee(s) selected for batch creation
                </Typography>
              </Box>
            )}
          </>
        ) : (
          <>
            <BatchForm
              onBack={() => setShowBatchForm(false)}
              onSubmit={handleBatchFormSubmit}
              initialData={batchFormData}
              availableRegions={selectedRegions}
            />
          </>
        )}

        {/* Email Modal */}
        {trainingTypeTabs[activeTab]?.value === trainingTypes.PRODUCT_FINANCING ? (
          <EmailModalProductFinancing
            open={showEmailModalPF}
            onClose={() => setShowEmailModalPF(false)}
            onSubmit={handleEmailSubmit}
            pfTrainerConfig={pfTrainerConfig}
          />
        ) : trainingTypeTabs[activeTab]?.value === trainingTypes.TRAILBLAZE_2_0 ? (
          <EmailModalTrailblaze
            open={showEmailModalTrailblaze}
            onClose={() => setShowEmailModalTrailblaze(false)}
            onSubmit={handleEmailSubmit}
            initialEmailConfig={emailConfig}
            trainingType={trainingTypeTabs[activeTab]?.label}
          />
        ) : trainingTypeTabs[activeTab]?.value === trainingTypes.COLLECTION_TRAINING ||
          trainingTypeTabs[activeTab]?.value === 'corrective-training' ? (
          <EmailModalCollection
            open={showEmailModalCollection}
            onClose={() => setShowEmailModalCollection(false)}
            onSubmit={handleEmailSubmit}
            initialEmailConfig={emailConfig}
            regions={selectedRegions}
            trainingType={trainingTypeTabs[activeTab]?.value}
            selectedEmployees={selectedEmployees}
            pendencyData={pendencyData}
            trainingTypeKey={trainingTypeTabs[activeTab]?.key}
          />
        ) : (
          <EmailModal
            open={showEmailModal}
            onClose={() => setShowEmailModal(false)}
            onSubmit={handleEmailSubmit}
            initialEmailConfig={emailConfig}
            regions={selectedRegions}
            trainingType={trainingTypeTabs[activeTab]?.value}
            selectedEmployees={selectedEmployees}
            pendencyData={pendencyData}
            trainingTypeKey={trainingTypeTabs[activeTab]?.key}
            invitationOnly={false}
          />
        )}

        {/* Review Modal */}
        <ReviewModal
          open={showReviewModal}
          onClose={() => setShowReviewModal(false)}
          onConfirm={handleCreateFinalBatch}
          batchData={batchFormData}
          emailConfig={emailConfig}
          selectedEmployees={selectedEmployees}
          trainingType={trainingTypeTabs[activeTab]}
          loading={loading || isCreatingBatch}
          pendencyData={pendencyData}
          onUpdateEmployees={setSelectedEmployees}
        />

        {/* Region Branch Selection Modal */}
        <RegionBranchModal
          open={showRegionBranchModal}
          onClose={() => setShowRegionBranchModal(false)}
          availableRegions={availableRegions}
          availableBranches={availableBranches}
          selectedRegions={selectedRegions}
          selectedBranches={selectedBranches}
          tableLoading={loading}
          tableAbortController={abortController}
          onRegionsChange={(newRegions) => {
            console.log('Regions changed, clearing selected employees');
            setSelectedRegions(newRegions);
            setSelectedEmployees({}); // Clear selected employees when regions change
            setManuallyAddedEmployees({}); // Clear manually added employees when regions change
          }}
          onBranchesChange={(newBranches) => {
            console.log('Branches changed, clearing selected employees');
            setSelectedBranches(newBranches);
            setSelectedEmployees({}); // Clear selected employees when branches change
            setManuallyAddedEmployees({}); // Clear manually added employees when branches change
          }}
          onSave={async (updatedRegions, updatedBranches) => {
            console.log(
              'RegionBranchModal onSave: Updating regions and branches, then fetching pendency data'
            );
            await fetchPendencyData(updatedRegions, updatedBranches);
            setShowRegionBranchModal(false);
          }}
        />

        {/* Add Employee Modal */}
        <Dialog
          open={showAddEmployeeModal}
          onClose={() => setShowAddEmployeeModal(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Add New Employee to Batch</DialogTitle>
          <DialogContent>
            <AddEmployeeForm
              batchIdReceived={null} // No batch ID since we're creating a new batch
              onSubmit={handleAddNewEmployee}
              closeModal={() => setShowAddEmployeeModal(false)}
              previewMode={true} // Add this prop to prevent API calls
            />
          </DialogContent>
        </Dialog>
      </Box>
    </Layout>
  );
};

// Batch Form Component (Inline)
const BatchForm = ({ onBack, onSubmit, initialData, availableRegions }) => {
  const [formData, setFormData] = useState({
    batchType: 'offline',
    trainingType: '',
    trainingRoLocation: '',
    address: '',
    googleMapsLink: '',
    trainingLink: '',
    duration: '',
    description: '',
    dates: [
      {
        day: 'Day 1',
        date: (() => {
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
          return tomorrow.toISOString().split('T')[0] + 'T09:00';
        })(),
      },
    ],
    datesEnd: [{ date: '18:30' }],
  });

  // Batch type options
  const batchTypeOptions = [
    { value: 'offline', label: 'Offline Training' },
    { value: 'online', label: 'Online Training' },
  ];

  // Duration options
  const durationOptions = [
    { value: '1-day', label: '1 Day' },
    { value: '2-days', label: '2 Days' },
    { value: '3-days', label: '3 Days' },
    { value: '4-days', label: '4 Days' },
    { value: '5-days', label: '5 Days' },
  ];

  // Reset form when component mounts
  useEffect(() => {
    // Set default values if initialData is empty or missing fields
    const defaultData = {
      batchType: 'offline',
      trainingType: '',
      trainingRoLocation: '',
      address: '',
      googleMapsLink: '',
      trainingLink: '',
      duration: '',
      description: '',
      dates: [
        {
          day: 'Day 1',
          date: (() => {
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            return tomorrow.toISOString().split('T')[0] + 'T09:00';
          })(),
        },
      ],
      datesEnd: [{ date: '18:30' }],
    };

    // Merge with initialData if it exists
    const mergedData = initialData ? { ...defaultData, ...initialData } : defaultData;
    setFormData(mergedData);
  }, [initialData, availableRegions]);

  const handleDurationChange = (selectedDuration) => {
    try {
      const days = parseInt(selectedDuration.split('-')[0], 10) || 1;
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const newDates = Array.from({ length: days }, (_, index) => {
        const date = new Date(tomorrow);
        date.setDate(tomorrow.getDate() + index);
        return {
          day: `Day ${index + 1}`,
          date: date.toISOString().split('T')[0] + 'T09:00',
        };
      });
      const newEndDates = Array.from({ length: days }, () => ({ date: '18:30' }));

      const updatedFormData = {
        ...formData,
        duration: selectedDuration,
        dates: newDates,
        datesEnd: newEndDates,
      };

      setFormData(updatedFormData);
    } catch (error) {
      console.error('Error in handleDurationChange:', error);
    }
  };

  const handleInputChange = (field, value) => {
    try {
      const updatedFormData = { ...formData, [field]: value };
      setFormData(updatedFormData);
    } catch (error) {
      console.error('Error in handleInputChange:', error);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
      {/* Header with Back Button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 4,
          p: 2,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 1,
        }}
      >
        <Button
          variant="outlined"
          onClick={onBack}
          startIcon={<ArrowUpwardIcon />}
          sx={{ mr: 3, px: 3, py: 1 }}
        >
          Back to Employee Selection
        </Button>
      </Box>

      {/* Form */}
      <Paper
        sx={{
          p: 4,
          borderRadius: 3,
          boxShadow: 3,
          bgcolor: 'background.paper',
        }}
      >
        <Box component="form" onSubmit={handleSubmit}>
          {/* Basic Information Section */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              sx={{
                mb: 3,
                color: 'primary.main',
                fontWeight: 600,
                pb: 1,
                borderBottom: '2px solid',
                borderColor: 'primary.main',
              }}
            >
              Basic Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={batchTypeOptions}
                  value={
                    batchTypeOptions.find((option) => option.value === formData.batchType) || null
                  }
                  onChange={(event, newValue) =>
                    handleInputChange('batchType', newValue?.value || 'offline')
                  }
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Batch Type"
                      required
                      variant="outlined"
                      size="medium"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: 'primary.main',
                          },
                        },
                      }}
                    />
                  )}
                  noOptionsText="No batch types available"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={availableRegions || []}
                  value={
                    availableRegions?.find(
                      (region) => region.regionName === formData.trainingRoLocation
                    ) || null
                  }
                  onChange={(event, newValue) =>
                    handleInputChange('trainingRoLocation', newValue?.regionName || '')
                  }
                  getOptionLabel={(option) => option.regionName || ''}
                  getOptionKey={(option) => option.regionId || option.regionName}
                  isOptionEqualToValue={(option, value) => option.regionId === value?.regionId}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Training RO Location"
                      required={formData.batchType === 'offline'}
                      variant="outlined"
                      size="medium"
                      placeholder="Select Training RO Location"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: 'primary.main',
                          },
                        },
                      }}
                    />
                  )}
                  noOptionsText="No regions available"
                  disabled={!availableRegions || availableRegions.length === 0}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={durationOptions}
                  value={
                    durationOptions.find((option) => option.value === formData.duration) || null
                  }
                  onChange={(event, newValue) => {
                    const selectedDuration = newValue?.value || '';
                    handleInputChange('duration', selectedDuration);
                    if (selectedDuration) {
                      handleDurationChange(selectedDuration);
                    }
                  }}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) => option.value === value?.value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Duration"
                      required
                      variant="outlined"
                      size="medium"
                      placeholder="Select Duration"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: 'primary.main',
                          },
                        },
                      }}
                    />
                  )}
                  noOptionsText="No duration options available"
                />
              </Grid>
            </Grid>
          </Box>
          {/* Training Schedule Section */}
          {formData.dates && formData.dates.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  color: 'primary.main',
                  fontWeight: 600,
                  pb: 1,
                  borderBottom: '2px solid',
                  borderColor: 'primary.main',
                }}
              >
                Training Schedule
              </Typography>
              <Grid container spacing={3}>
                {formData.dates.map((day, index) => (
                  <Grid item xs={12} key={index}>
                    <Box
                      sx={{
                        p: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        bgcolor: 'background.default',
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}
                      >
                        {day.day}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <TextField
                          label="Date & Start Time"
                          type="datetime-local"
                          value={day.date || ''}
                          onChange={(e) => {
                            const newDates = [...formData.dates];
                            newDates[index] = { ...day, date: e.target.value };
                            setFormData({ ...formData, dates: newDates });
                          }}
                          inputProps={{
                            min: new Date().toISOString().slice(0, 16),
                          }}
                          sx={{ flex: 1, minWidth: 250 }}
                          InputLabelProps={{ shrink: true }}
                          variant="outlined"
                          size="medium"
                        />
                        <TextField
                          label="End Time"
                          type="time"
                          value={formData.datesEnd?.[index]?.date || '18:30'}
                          onChange={(e) => {
                            const newEndDates = [...(formData.datesEnd || [])];
                            newEndDates[index] = { date: e.target.value };
                            setFormData({ ...formData, datesEnd: newEndDates });
                          }}
                          sx={{ width: 200 }}
                          InputLabelProps={{ shrink: true }}
                          variant="outlined"
                          size="medium"
                        />
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
          {/* Location/Link Section */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              sx={{
                mb: 3,
                color: 'primary.main',
                fontWeight: 600,
                pb: 1,
                borderBottom: '2px solid',
                borderColor: 'primary.main',
              }}
            >
              {formData.batchType === 'offline' ? 'Training Location' : 'Online Training Details'}
            </Typography>
            {formData.batchType === 'offline' ? (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Training Address"
                    value={formData.address || ''}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    multiline
                    rows={3}
                    required
                    variant="outlined"
                    placeholder="Enter the complete training venue address..."
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={
                      formData.batchType === 'offline'
                        ? 'Google Maps Link *'
                        : 'Google Maps Link (Optional)'
                    }
                    value={formData.googleMapsLink || ''}
                    onChange={(e) => handleInputChange('googleMapsLink', e.target.value)}
                    placeholder="https://maps.google.com/..."
                    required={formData.batchType === 'offline'}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                  />
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Online Training Link"
                    value={formData.trainingLink || ''}
                    onChange={(e) => handleInputChange('trainingLink', e.target.value)}
                    placeholder="https://meet.google.com/... or https://zoom.us/..."
                    required
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                  />
                </Grid>
              </Grid>
            )}
          </Box>

          {/* Description Section */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              sx={{
                mb: 3,
                color: 'primary.main',
                fontWeight: 600,
                pb: 1,
                borderBottom: '2px solid',
                borderColor: 'primary.main',
              }}
            >
              Additional Details
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Training Description"
                  value={formData.description || ''}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  multiline
                  rows={3}
                  variant="outlined"
                  placeholder="Enter any additional details about the training..."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Submit Button */}
          <Box
            sx={{
              mt: 5,
              display: 'flex',
              justifyContent: 'center',
              pt: 3,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Button
              type="submit"
              variant="contained"
              size="large"
              sx={{
                px: 6,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: 2,
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 4,
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              Next: Configure Email
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

// Review Modal Component
const ReviewModal = ({
  open,
  onClose,
  onConfirm,
  batchData,
  emailConfig,
  selectedEmployees,
  trainingType,
  loading,
  pendencyData,
  onUpdateEmployees,
}) => {
  const [showEmployeeManager, setShowEmployeeManager] = useState(false);
  const [employeeStatuses, setEmployeeStatuses] = useState({});

  const getTotalSelectedEmployees = () => {
    return Object.values(selectedEmployees).reduce(
      (total, employees) => total + employees.length,
      0
    );
  };

  const handleEmployeeStatusChange = (employeeId, status) => {
    setEmployeeStatuses((prev) => ({
      ...prev,
      [employeeId]: status,
    }));
  };

  const handleRemoveEmployee = (trainingTypeKey, employeeId) => {
    const newSelected = { ...selectedEmployees };
    if (newSelected[trainingTypeKey]) {
      newSelected[trainingTypeKey] = newSelected[trainingTypeKey].filter((id) => id !== employeeId);
      onUpdateEmployees(newSelected);
    }
  };

  const renderEmployeeManager = () => {
    const currentTrainingTypeKey = trainingType.key;
    const selectedEmployeeIds = selectedEmployees[currentTrainingTypeKey] || [];
    const employees = pendencyData[currentTrainingTypeKey] || [];
    const selectedEmployeesData = employees.filter((emp) =>
      selectedEmployeeIds.includes(emp.employeeId)
    );

    return (
      <Dialog
        open={showEmployeeManager}
        onClose={() => setShowEmployeeManager(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Manage Batch Employees</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              You can mark employees as absconding, DNJ, or resigned, or remove them from the batch.
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Employee ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedEmployeesData.map((employee) => (
                    <TableRow key={employee.employeeId}>
                      <TableCell>{employee.employeeId}</TableCell>
                      <TableCell>{employee.name}</TableCell>
                      <TableCell>
                        <Autocomplete
                          options={[
                            { value: 'active', label: 'Active' },
                            { value: 'absconding', label: 'Absconding' },
                            { value: 'dnj', label: 'DNJ' },
                            { value: 'resigned', label: 'Resigned' },
                            { value: 'rejoined', label: 'Rejoined' },
                          ]}
                          value={{
                            value: employeeStatuses[employee.employeeId] || 'active',
                            label: (() => {
                              const status = employeeStatuses[employee.employeeId] || 'active';
                              switch (status) {
                                case 'active':
                                  return 'Active';
                                case 'absconding':
                                  return 'Absconding';
                                case 'dnj':
                                  return 'DNJ';
                                case 'resigned':
                                  return 'Resigned';
                                default:
                                  return 'Active';
                              }
                            })(),
                          }}
                          onChange={(event, newValue) =>
                            handleEmployeeStatusChange(
                              employee.employeeId,
                              newValue?.value || 'active'
                            )
                          }
                          getOptionLabel={(option) => option.label}
                          isOptionEqualToValue={(option, value) => option.value === value.value}
                          renderInput={(params) => (
                            <TextField {...params} size="small" sx={{ minWidth: 120 }} />
                          )}
                          sx={{ minWidth: 120 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          color="error"
                          onClick={() =>
                            handleRemoveEmployee(currentTrainingTypeKey, employee.employeeId)
                          }
                        >
                          Remove
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEmployeeManager(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Review Batch Details</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
              Batch Information
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Training Type:
                </Typography>
                <Typography variant="body1">
                  {batchData.trainingType || trainingType.label}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Batch Type:
                </Typography>
                <Typography variant="body1">{batchData.batchType}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Training RO Location:
                </Typography>
                <Typography variant="body1">{batchData.trainingRoLocation}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Duration:
                </Typography>
                <Typography variant="body1">{batchData.duration}</Typography>
              </Grid>
              {batchData.batchType === 'offline' ? (
                <>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Address:
                    </Typography>
                    <Typography variant="body1">{batchData.address}</Typography>
                  </Grid>
                  {batchData.googleMapsLink && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Google Maps Link:
                      </Typography>
                      <Typography
                        variant="body1"
                        component="a"
                        href={batchData.googleMapsLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {batchData.googleMapsLink}
                      </Typography>
                    </Grid>
                  )}
                </>
              ) : (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Link for Online Training:
                  </Typography>
                  <Typography
                    variant="body1"
                    component="a"
                    href={batchData.trainingLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {batchData.trainingLink}
                  </Typography>
                </Grid>
              )}
              {batchData.description && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Description:
                  </Typography>
                  <Typography variant="body1">{batchData.description}</Typography>
                </Grid>
              )}
              {batchData.dates && batchData.dates.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Training Schedule:
                  </Typography>
                  {batchData.dates.map((day, index) => (
                    <Box key={index} sx={{ mb: 1, pl: 2 }}>
                      <Typography variant="body2">
                        {day.day}: {new Date(day.date).toLocaleDateString()} at{' '}
                        {new Date(day.date).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}{' '}
                        - {batchData.datesEnd?.[index]?.date || '18:30'}
                      </Typography>
                    </Box>
                  ))}
                </Grid>
              )}
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
              Email Configuration
            </Typography>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                Total Steps: {emailConfig.totalSteps}
              </Typography>
              {emailConfig.steps.map((step, index) => (
                <Box key={index} sx={{ mb: 1, pl: 2 }}>
                  <Typography variant="body2">
                    Step {index + 1}: {step.to.length} recipients, {step.cc.length} CC
                  </Typography>
                </Box>
              ))}
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
              Selected Employees
            </Typography>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
            >
              <Typography variant="body1">
                Total: {getTotalSelectedEmployees()} employees
              </Typography>
              {/* <Button
                variant="outlined"
                size="small"
                onClick={() => setShowEmployeeManager(true)}
              >
                Manage Employees
              </Button> */}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={onConfirm} variant="contained" disabled={loading}>
            {loading ? 'Creating Batch...' : 'Create Batch'}
          </Button>
        </DialogActions>
      </Dialog>
      {renderEmployeeManager()}
    </>
  );
};

// Region Branch Selection Modal Component
const RegionBranchModal = ({
  open,
  onClose,
  availableRegions,
  availableBranches,
  selectedRegions,
  selectedBranches,
  onRegionsChange,
  onBranchesChange,
  onSave,
  tableLoading,
  tableAbortController,
}) => {
  const [tempSelectedRegions, setTempSelectedRegions] = useState(selectedRegions);
  const [tempSelectedBranches, setTempSelectedBranches] = useState(selectedBranches);
  const [loading, setLoading] = useState(false);
  const [abortController, setAbortController] = useState(null);

  useEffect(() => {
    console.log('=== useEffect triggered ===');
    console.log('open:', open);
    console.log('selectedRegions:', selectedRegions);
    console.log('selectedBranches:', selectedBranches);

    if (open) {
      console.log('Modal opened, setting temp states:', { selectedRegions, selectedBranches });
      setTempSelectedRegions(selectedRegions);
      setTempSelectedBranches(selectedBranches);
    } else {
      // Abort any pending requests when modal closes
      if (abortController) {
        abortController.abort();
        setAbortController(null);
      }
    }
    console.log('=== end useEffect ===');
  }, [open, selectedRegions, selectedBranches]);
  console.log('tableAbortController', tableAbortController);
  // Separate useEffect for cleanup
  useEffect(() => {
    return () => {
      if (abortController) {
        abortController.abort();
      }
      // Ensure loading is set to false when component unmounts
      console.log('RegionBranchModal unmounting: Setting loading to false');
      setLoading(false);
    };
  }, [abortController]);

  const handleRegionChange = async (event, newValue) => {
    console.log('=== handleRegionChange called ===');
    console.log('event:', event);
    console.log('newValue:', newValue);
    console.log('newValue length:', newValue?.length);
    console.log('Previous tempSelectedRegions:', tempSelectedRegions);
    console.log('Previous tempSelectedRegions length:', tempSelectedRegions?.length);
    console.log('Setting new tempSelectedRegions to:', newValue);
    console.log('=== end handleRegionChange ===');
    setTempSelectedRegions(newValue);

    // Abort any previous request
    if (abortController) {
      abortController.abort();
    }

    // If regions changed, fetch new branches for selected regions
    if (newValue && newValue.length > 0) {
      let newAbortController = null;
      try {
        // Create new AbortController for this request
        newAbortController = new AbortController();
        setAbortController(newAbortController);

        // Set loading to true for new request
        console.log('handleRegionChange: Setting loading to true');
        setLoading(true);

        const regionIds = newValue.map((region) => region.regionId);
        const branchesResponse = await rootAxios.post(
          '/user/getRegionBatches',
          {
            regions: regionIds,
          },
          {
            signal: newAbortController.signal,
          }
        );

        // Check if request was aborted
        if (newAbortController.signal.aborted) {
          console.log('Branch fetch request was aborted');
          console.log(
            'handleRegionChange: Request aborted, setting loading to false and returning'
          );
          setLoading(false);
          return;
        }

        if (branchesResponse?.data?.data) {
          const newBranches = branchesResponse.data.data;
          setTempSelectedBranches(newBranches);
        } else {
          setTempSelectedBranches([]);
        }
      } catch (error) {
        // Don't show error if request was aborted
        if (error.name === 'AbortError' || error?.name === 'CanceledError') {
          console.log('Branch fetch request was aborted');
          setLoading(false);
          return;
        }
        console.error('Error fetching branches for selected regions:', error);
        toast.error('Failed to fetch branches for selected regions');
        setTempSelectedBranches([]);
        console.log('handleRegionChange: Setting loading to false in catch block');
        setLoading(false);
      } finally {
        // Always ensure loading state is properly managed
        // Set loading to false when request completes (regardless of abort state)
        console.log('handleRegionChange: Setting loading to false in finally block');
        setLoading(false);
      }
    } else {
      // Clear branches when no regions are selected
      setTempSelectedBranches([]);
    }
  };

  const handleSave = async () => {
    onRegionsChange(tempSelectedRegions);
    onBranchesChange(tempSelectedBranches);
    await onSave(tempSelectedRegions, tempSelectedBranches);
  };

  const handleCancel = () => {
    setTempSelectedRegions(selectedRegions);
    setTempSelectedBranches(selectedBranches);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle>Select Regions and Branches</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                Regions
              </Typography>
              <Autocomplete
                multiple
                options={availableRegions}
                value={tempSelectedRegions || []}
                onChange={handleRegionChange}
                getOptionLabel={(option) => option.regionName || ''}
                getOptionKey={(option) => option.regionId || option.regionName}
                isOptionEqualToValue={(option, value) => {
                  const result = option.regionId === value.regionId;
                  return result;
                }}
                onOpen={() =>
                  console.log('Autocomplete opened, availableRegions:', availableRegions)
                }
                onClose={() =>
                  console.log(
                    'Autocomplete closed, current tempSelectedRegions:',
                    tempSelectedRegions
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Regions"
                    placeholder="Choose regions..."
                    required
                  />
                )}
                renderTags={(value, getTagProps) =>
                  (value || []).map((option, index) => (
                    <Chip
                      label={option.regionName || ''}
                      {...getTagProps({ index })}
                      key={option.regionId}
                    />
                  ))
                }
                noOptionsText="No regions available"
                loading={false}
                disableCloseOnSelect={false}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                Branches
              </Typography>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <Autocomplete
                  multiple
                  options={availableBranches}
                  value={tempSelectedBranches}
                  onChange={(event, newValue) => setTempSelectedBranches(newValue)}
                  getOptionLabel={(option) => option.BRANCH_NAME || ''}
                  getOptionKey={(option) => option.BRANCH_ID || option.BRANCH_NAME}
                  isOptionEqualToValue={(option, value) => option.BRANCH_ID === value.BRANCH_ID}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Branches"
                      placeholder="Choose branches..."
                      required
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option.BRANCH_NAME || ''}
                        {...getTagProps({ index })}
                        key={option.BRANCH_ID}
                      />
                    ))
                  }
                />
              )}
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        {/* <Button onClick={() => tableAbortController?.abort()}>Reset</Button> */}
        {/* {tableLoading ? (
          <CircularProgress size={24} />
        ) : ( */}
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={tempSelectedRegions.length === 0 || tempSelectedBranches.length === 0}
        >
          Save & Refresh Pendency
        </Button>
        {/* )} */}
      </DialogActions>
    </Dialog>
  );
};

export default CreateBatch;
